import React, { FC, useState, useEffect, useContext } from 'react';
import {
    PrimaryButton,
    Callout,
    getTheme,
    Stack,
    DirectionalHint,
    mergeStyleSets,
    Label,
    Spinner,
    IIconProps,
    SpinnerSize
} from '@fluentui/react';
import { TextField } from '@eta/components';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { inviteUser } from '../Redux/Actions';
import { ReduxState } from '../../common/reducers';
import { isEmptyText } from '../../helpers/utils';
import './InviteUser.scss';
import { UIHelpers } from '../../helpers/UIHelpers';
import { ServiceCreator } from '../../Services/ClientProxy/ServiceFactory';
import { ApiException, ResponseStatus, UserDetailsDto } from '../../Services/ClientProxy/NationalIdRegistryClient';
import { Defaults } from '../../common/config/constants';
import { RepresentationContext } from './InviteUser/Context/RepresentationContext';
import { WizardStateAndLogic } from '../../common/component/WizardPivot';
import { InviteUserMode, inviteUserWizardKey } from './InviteUser/InviteUserMain';

const mapDispatchToProps = {
    inviteUser,
};
const theme = getTheme();
const styles = mergeStyleSets({
    callout: {
        maxWidth: 500,
    },
    contentcallout: {
        fontsize: '14px',
        padding: '24px 20px',
        color: theme.palette.neutralPrimaryAlt,
    },
});

const mapStateToProps = (state: ReduxState) => ({
    lastError: state.invitation.lastError,
    currentLanguage: state.reduxlanguage.language,
});

export interface InviteUserProps {
    InviteUserWizard: WizardStateAndLogic;
    setErrorMsg: (msg: string | undefined) => void;
    setSuccessMsg: (msg: string | undefined) => void;
    mode?: InviteUserMode;
    clientId?: string;
}
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const InviteUser: FC<PropsFromRedux & InviteUserProps> = (
    props: PropsFromRedux & InviteUserProps
) => {
    const { lastError, InviteUserWizard, setErrorMsg, setSuccessMsg, mode, clientId } = props;
    const { state: currentInvitation, dispatch: dispatchInvitationRequest } =
        useContext(RepresentationContext);
    const [loading, setIsLoading] = useState<boolean>(false);
    const { t } = useTranslation(['resources']);
    const [isCalloutVisible, setIsCalloutVisible] = useState<boolean>(
        false
    );
    const [editMode, setEditMode] = useState<boolean>(
        false
    );
    const [nationalId, setNationalId] = useState<string | null>('');

    const [apiNationalIdError, setApiNationalIdError] = useState<string>();

    const [ggEnable, setGGEnable] = useState<boolean>(
        false
    );
    const [validate, setValidate] = useState<boolean>(
        false
    );
    const search: IIconProps = { iconName: 'Search' };

    const isValidEmail = () => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(currentInvitation?.email || '');
    const isValidForm = () => {
        // eslint-disable-next-line no-useless-escape
        if (isEmptyText(currentInvitation?.nationalID) ||
            isEmptyText(currentInvitation?.email) ||
            isEmptyText(currentInvitation?.nameSecondaryLang) ||
            isEmptyText(currentInvitation?.surnameSecondaryLang) ||
            !isValidEmail()) {
            return (false);
        }
        return (true);
    };

    useEffect(() => {
        if (mode === 'Edit' ||
            (InviteUserWizard?.state.selectedTabKey &&
                InviteUserWizard?.state.selectedTabKey !==
                inviteUserWizardKey.UserInfo)
        ) {
            setValidate(true);
            InviteUserWizard.ChangeTabState(
                inviteUserWizardKey.UserInfo,
                isValidForm() ? 'completed' : 'error'
            );
        }
    }, [InviteUserWizard?.state.selectedTabKey]);

    // const saveForm = async () => {
    //     let message = '';
    //     if (isEmptyText(currentRequest?.nameSecondaryLang)) {
    //         message = `${t('FirstNameARisRequired')}`;
    //     } if (isEmptyText(currentRequest?.surnameSecondaryLang)) {
    //         message = `${message} \n ${t('LastNameARisRequired')}`;
    //     } if (isEmptyText(currentRequest?.nationalID)) {
    //         message = `${message} \n ${t('NationalIDisRequired')}`;
    //     } if (isEmptyText(currentRequest?.email)) {
    //         message = `${message} \n ${t('EmailisRequired')}`;
    //     } if (isNullOrUndefined(currentRequest?.dateActivate)) {
    //         message = `${message} \n ${t('ActivefromdateisRequired')}`;
    //     } if (isNullOrUndefined(currentRequest?.isAdministrator)) {
    //         message = `${message} \n ${t('UsertypeisRequired')}`;
    //     }

    //     if (isEmptyText(message) && currentRequest?.dateActivate) {
    //         if(!activeFromCheck && currentRequest?.dateActivate > new Date()) {
    //             setActiveFromCheck(true);
    //             setError('');
    //         } else {
    //         setIsLoading(true);
    //         const request = new RegisterDelegateDto();
    //         request.preferredLanguage = language;
    //         request.userProfileType = ProfileTypes.User;

    //         request.representationInvitation = currentRequest;
    //         const response = await props.inviteUser(request);
    //         if (isNull((await response.payload).lastError)) {
    //             props.onSave();
    //         } else {
    //             setError(
    //                 (
    //                     await response.payload
    //                 ).lastError?.serviceError?.toString() || ''
    //             );
    //             setSuccess('');
    //         }
    //         setIsLoading(false);
    //     }
    //     } else {
    //         setError(message);
    //         setSuccess('');
    //     }
    // };

    const SetUserInfo = (userDetails: UserDetailsDto | undefined, natID: string | undefined) => {
        dispatchInvitationRequest({
            type: 'updateRepresentation',
            payload: {
                nameSecondaryLang: userDetails?.firstName,
                surnameSecondaryLang: userDetails?.otherNames,
                nationalID: natID,
            },
        });
    };

    const RefillData = (userDetails: UserDetailsDto, natID: string) => {
        switch (userDetails?.status) {
            case ResponseStatus.Valid:
                SetUserInfo(userDetails, natID);
                setEditMode(true);
                setGGEnable(userDetails.ggEnable);
                setSuccessMsg(t('NationalIDRegistry_Success'));
                break;
            case ResponseStatus.DeadPerson:
                setApiNationalIdError(t('NationalID_DeadPerson'));
                break;
            case ResponseStatus.Expired:
                setApiNationalIdError(t('NationalID_Expired'));
                break;
            case ResponseStatus.NotFound:
                setApiNationalIdError(t('NationalID_NotFound'));
                break;
            case ResponseStatus.Suspended:
                setApiNationalIdError(t('NationalID_Suspended'));
                break;
            case ResponseStatus.None:
                if (!userDetails.ggEnable) {
                    setEditMode(true);
                    setGGEnable(userDetails.ggEnable);
                    SetUserInfo(undefined, natID);
                } else SetUserInfo(undefined, undefined);
                break;
            default:
                SetUserInfo(undefined, undefined);
                break;
        }
    };

    const SearchANdFill = async () => {
        SetUserInfo(undefined, undefined);
        if (nationalId) {
            const isnum = /^\d+$/.test(nationalId);
            if (!(isnum && nationalId.length === Defaults.NationalIDlength)) {
                setApiNationalIdError(t('NationalID_NotValid'));
                return;
            }
            try {
                setIsLoading(true);
                const client = ServiceCreator.getNationalIdRegistryClient();
                const result = await client.getUserByNatID(nationalId);
                RefillData(result, nationalId);
                setErrorMsg('');
            } catch (ex) {
                setEditMode(false);
                setGGEnable(false);
                setSuccessMsg('');
                const exception = ex as ApiException;
                if (exception.status === 429) {
                    setErrorMsg(t('NumberRequestsExceedlimit'));
                } else {
                    const errors = UIHelpers.GetErrors(exception);
                    if (errors.NatID) {
                        setApiNationalIdError(errors.NatID?.toString());
                    } else {
                        setErrorMsg(exception?.response?.toString());
                    }
                }
            }
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setErrorMsg(lastError?.serviceError?.toString());
        setSuccessMsg('');
    }, [lastError]);

    return (
        <div className="inviteUser">
            {mode === 'Edit' || mode === 'View' ? (
                <div className='personal_details'>
                    <div className="bodyLine InvitationSolidLine">
                        <Label>{t('form.TaxPayer.NationalID')}</Label>
                        <Label>{currentInvitation.nationalID}</Label>
                    </div>
                    <div className="bodyLine InvitationSolidLine">
                        <Label>{t('form.TaxPayer.FirstNameAR')}</Label>
                        <Label>{currentInvitation?.nameSecondaryLang}</Label>
                    </div>
                    <div className="bodyLine InvitationSolidLine">
                        <Label>{t('UserProfile.SurnameAR')}</Label>
                        <Label>{currentInvitation?.surnameSecondaryLang}</Label>
                    </div>

                    <div className="bodyLine InvitationSolidLine">
                        <Label>{t('form.TaxPayer.FirstNameEN')}</Label>
                        <Label>{currentInvitation?.namePrimaryLang}</Label>
                    </div>
                    <div className="bodyLine InvitationSolidLine">
                        <Label>{t('UserProfile.SurnameEN')}</Label>
                        <Label>{currentInvitation?.surnamePrimaryLang}</Label>
                    </div>
                    <div className="bodyLine InvitationSolidLine">
                        <Label>{t('form.TaxPayer.Email')}</Label>
                        <Label>{clientId}</Label>
                    </div>

                </div>
            )
                : (
                    <>
                        <div className='bodyLine  InvitationSolidLine'>
                            <div className='Id_btn'>
                                <div className="personalData btn-personalData">
                                    <TextField
                                        className='field_personalId'
                                        name="NationalId"
                                        id="NationalId"
                                        onChange={(_, val) => {
                                            setNationalId(
                                                val ?? ''
                                            );
                                            setApiNationalIdError(undefined);
                                        }
                                        }
                                        required
                                        label={t('form.components.nationalId')}
                                        onFocus={() => { setIsCalloutVisible(true); }}
                                        value={nationalId ?? ''}
                                        maxLength={14}
                                    />
                                </div>
                                <div className="btn-search">
                                    <PrimaryButton
                                        className="search"
                                        iconProps={search}
                                        text={t('searchAndFill')}
                                        disabled={!nationalId?.trim()}
                                        onClick={SearchANdFill}
                                    />
                                </div>
                            </div>

                            <div />
                            <div className='invitation_NationalIDErrorr'>
                                {apiNationalIdError && (
                                    <span className="error">
                                        {apiNationalIdError}
                                    </span>
                                )}
                                {validate && isEmptyText(currentInvitation?.nationalID) && (
                                    <div className='ErrorMessage'>
                                        {t('NationalIDisRequired')}
                                    </div>
                                  )}
                            </div>

                        </div>
                        <div>
                            {isCalloutVisible && (
                                <Callout
                                    setInitialFocus
                                    className={styles.callout}
                                    target="#NationalId"
                                    onDismiss={() => { setIsCalloutVisible(false); }}
                                    role="alertdialog"
                                    gapSpace={0}
                                    doNotLayer={false}
                                    directionalHint={DirectionalHint.bottomLeftEdge}
                                >
                                    <Stack
                                        horizontalAlign="start"
                                    >
                                        <div className={styles.contentcallout}>
                                            <span className='boldinvitation_ImpMsg'>  {t('invitation_ImpMsg1')} </span>
                                            <span> {t('invitation_ImpMsg2')}</span>
                                        </div>
                                    </Stack>
                                </Callout>
                            )}
                        </div>
                        <div className='personal_details'>

                            {ggEnable ? (
                                <>
                                    <div className={editMode ? 'bodyLine InvitationSolidLine' : 'bodyLine InvitationSolidLine opacity'}>
                                        <Label>{t('form.TaxPayer.NationalID')}</Label>
                                        <Label>{currentInvitation.nationalID}</Label>
                                    </div>
                                    <div className={editMode ? 'bodyLine InvitationSolidLine' : 'bodyLine InvitationSolidLine opacity'}>
                                        <Label>{t('form.TaxPayer.FirstNameAR')}</Label>
                                        <Label>{currentInvitation?.nameSecondaryLang}</Label>
                                    </div>
                                    <div className={editMode ? 'bodyLine InvitationSolidLine' : 'bodyLine InvitationSolidLine opacity'}>
                                        <Label>{t('UserProfile.SurnameAR')}</Label>
                                        <Label>{currentInvitation?.surnameSecondaryLang}</Label>
                                    </div>
                                </>
                            ) : (
                                <div className="invitation_Information">
                                    <TextField
                                        label={t('form.TaxPayer.FirstNameAR')}
                                        value={currentInvitation?.nameSecondaryLang}
                                        onChange={(_, v) =>
                                            // setCurrentRequest(
                                            //     new RepresentationInvitationDto({
                                            //         ...currentRequest,
                                            //         nameSecondaryLang: v ?? '',
                                            //     })
                                            // )
                                            dispatchInvitationRequest({
                                                type: 'updateRepresentation',
                                                payload: {
                                                    nameSecondaryLang: v ?? '',
                                                },
                                            })
                                        }
                                        required
                                        disabled={!editMode}
                                    />


                                    <TextField
                                        label={t('UserProfile.SurnameAR')}
                                        value={currentInvitation?.surnameSecondaryLang}
                                        onChange={(_, v) =>
                                            dispatchInvitationRequest({
                                                type: 'updateRepresentation',
                                                payload: {
                                                    surnameSecondaryLang: v ?? '',
                                                },
                                            })
                                        }
                                        required
                                        disabled={!editMode}
                                    />
                                    {validate && isEmptyText(currentInvitation?.nameSecondaryLang) && (
                                        <div className='ErrorMessage'>
                                            {t('FirstNameARisRequired')}
                                        </div>
                                      )}
                                    {validate && isEmptyText(currentInvitation?.surnameSecondaryLang) && (
                                        <div className='ErrorMessage'>
                                            {t('LastNameARisRequired')}
                                        </div>
                                      )}
                                </div>
                            )}
                        </div>

                        <div className="bodyLine">
                            <TextField
                                label={t('form.TaxPayer.FirstNameEN')}
                                value={currentInvitation?.namePrimaryLang}
                                onChange={(_, v) =>
                                    dispatchInvitationRequest({
                                        type: 'updateRepresentation',
                                        payload: {
                                            namePrimaryLang: v ?? '',
                                        },
                                    })
                                }
                                disabled={!editMode}
                            />
                            <TextField
                                label={t('UserProfile.SurnameEN')}
                                value={currentInvitation?.surnamePrimaryLang}
                                onChange={(_, v) =>
                                    dispatchInvitationRequest({
                                        type: 'updateRepresentation',
                                        payload: {
                                            surnamePrimaryLang: v ?? '',
                                        },
                                    })
                                }
                                disabled={!editMode}
                            />
                        </div>

                        <div className="bodyLine">
                            <TextField
                                label={t('form.TaxPayer.Email')}
                                value={currentInvitation?.email}
                                onChange={(_, v) =>
                                    dispatchInvitationRequest({
                                        type: 'updateRepresentation',
                                        payload: {
                                            email: v ?? '',
                                        },
                                    })
                                }
                                required
                                disabled={!editMode}
                            />
                        </div>
                        {validate && isEmptyText(currentInvitation?.email) && (
                            <div className='ErrorMessage'>
                                {t('EmailisRequired')}
                            </div>
                          )}
                        {validate && !isEmptyText(currentInvitation?.email) && !isValidEmail() && (
                            <div className='ErrorMessage'>
                                {t('EmailAddressErrorMessage')}
                            </div>
                          )}
                    </>
                )}
            {loading && (
                <div className="spinnerOnButtonCLick">
                    <Spinner
                        size={SpinnerSize.large}
                        label={t('form.TaxPayer.loading')}
                        ariaLive="assertive"
                        labelPosition="bottom"
                    />
                </div>
            )}
        </div>
    );
};

export default connector(InviteUser);
